/* ==========================================================================
   NGA — Size guide
   --------------------------------------------------------------------------
   Three jobs: switch category tabs, swap inches for centimetres, and suggest
   a size from entered measurements.

   The finder deliberately reads the open chart's own column headings rather
   than carrying its own copy of the sizing. That way it can never drift out
   of step with the table a shopper is looking at, and a chart that is not
   bust/waist/hip (the belts, the Lush 'Unders') simply offers the fields it
   does have — or steps aside entirely.
   ========================================================================== */

(function () {
  'use strict';

  var FIELDS = ['bust', 'waist', 'hip'];

  function parseRange(text) {
    // "38-40", "38 – 40", "38" → {min, max}. Returns null when there is no number.
    if (!text) return null;
    var nums = String(text).replace(/[–—]/g, '-').match(/\d+(\.\d+)?/g);
    if (!nums || !nums.length) return null;
    var a = parseFloat(nums[0]);
    var b = nums.length > 1 ? parseFloat(nums[1]) : a;
    return { min: Math.min(a, b), max: Math.max(a, b) };
  }

  function init(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-nga-sg-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-nga-sg-panel]'));
    var finder = root.querySelector('[data-nga-sg-finder]');

    /* ---------- Tabs ---------- */

    function activate(index) {
      tabs.forEach(function (t, i) {
        var on = i === index;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
      panels.forEach(function (p, i) {
        var on = i === index;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      });
      clearResult();
      syncFinderFields();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { activate(i); });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        if (next === null) return;
        e.preventDefault();
        activate(next);
        tabs[next].focus();
      });
    });

    /* ---------- Inches / centimetres ---------- */

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-nga-sg-unit]');
      if (!btn) return;
      var panel = btn.closest('[data-nga-sg-panel]');
      if (!panel) return;
      var unit = btn.getAttribute('data-nga-sg-unit');

      panel.querySelectorAll('[data-nga-sg-unit]').forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
      });
      panel.querySelectorAll('[data-nga-sg-body]').forEach(function (body) {
        body.hidden = body.getAttribute('data-nga-sg-body') !== unit;
      });

      // The table and the finder should never disagree about units.
      setFinderUnit(unit);
      clearResult();
    });

    /* ---------- Fit finder ---------- */

    if (!finder) return;

    var form = finder.querySelector('[data-nga-sg-finder-form]');
    var sizeEl = finder.querySelector('[data-nga-sg-result-size]');
    var noteEl = finder.querySelector('[data-nga-sg-result-note]');
    var unitBtns = Array.prototype.slice.call(finder.querySelectorAll('[data-nga-sg-funit]'));
    var unit = 'in';

    function activePanel() {
      return panels.find(function (p) { return !p.hidden; }) || panels[0];
    }

    function headerIndexes(panel) {
      var ths = panel.querySelectorAll('thead th');
      var map = {};
      Array.prototype.forEach.call(ths, function (th, i) {
        var label = th.textContent.toLowerCase();
        FIELDS.forEach(function (f) {
          // "Hip (in)" and "Hips" both count as hip.
          if (map[f] === undefined && label.indexOf(f) > -1) map[f] = i;
        });
      });
      return map;
    }

    function setFinderUnit(u) {
      if (!finder) return;
      unit = u;
      unitBtns.forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-nga-sg-funit') === u);
      });
    }

    unitBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        var u = b.getAttribute('data-nga-sg-funit');
        setFinderUnit(u);
        // Move the table with it, so the numbers on screen match the input.
        var panel = activePanel();
        var tableBtn = panel && panel.querySelector('[data-nga-sg-unit="' + u + '"]');
        if (tableBtn && !tableBtn.classList.contains('is-active')) tableBtn.click();
        else clearResult();
      });
    });

    function clearResult() {
      if (!finder) return;
      if (sizeEl) sizeEl.innerHTML = '&mdash;';
      if (noteEl) noteEl.textContent = '';
      panels.forEach(function (p) {
        p.querySelectorAll('tr.is-match').forEach(function (r) { r.classList.remove('is-match'); });
      });
    }

    // Only ask for the measurements the open chart can actually match on.
    function syncFinderFields() {
      if (!finder) return;
      var panel = activePanel();
      if (!panel) return;
      var usable = panel.getAttribute('data-finder') === 'true';
      finder.classList.toggle('is-off', !usable);

      var map = usable ? headerIndexes(panel) : {};
      FIELDS.forEach(function (f) {
        var wrap = finder.querySelector('[data-nga-sg-field="' + f + '"]');
        if (wrap) wrap.hidden = map[f] === undefined;
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var panel = activePanel();
      if (!panel || panel.getAttribute('data-finder') !== 'true') return;

      var map = headerIndexes(panel);
      var entered = {};
      var any = false;
      FIELDS.forEach(function (f) {
        if (map[f] === undefined) return;
        var input = form.querySelector('[name="' + f + '"]');
        var v = input && parseFloat(input.value);
        if (!isNaN(v) && v > 0) { entered[f] = v; any = true; }
      });

      if (!any) {
        sizeEl.innerHTML = '&mdash;';
        noteEl.textContent = 'Enter at least one measurement.';
        return;
      }

      var body = panel.querySelector('[data-nga-sg-body="' + unit + '"]')
              || panel.querySelector('[data-nga-sg-body]');
      if (!body) return;

      var rows = Array.prototype.slice.call(body.querySelectorAll('tr'));
      var best = null;

      rows.forEach(function (row) {
        var cells = row.querySelectorAll('th, td');
        var hits = 0;
        var distance = 0;
        var comparable = 0;

        Object.keys(entered).forEach(function (f) {
          var cell = cells[map[f]];
          var range = cell && parseRange(cell.textContent);
          if (!range) return;
          comparable++;
          var v = entered[f];
          if (v >= range.min && v <= range.max) {
            hits++;
          } else {
            distance += v < range.min ? range.min - v : v - range.max;
          }
        });

        if (!comparable) return;
        var score = { row: row, hits: hits, distance: distance };
        if (!best || score.hits > best.hits || (score.hits === best.hits && score.distance < best.distance)) {
          best = score;
        }
      });

      clearResult();
      if (!best) {
        noteEl.textContent = 'We could not read this chart. Please contact the fit team.';
        return;
      }

      best.row.classList.add('is-match');
      sizeEl.textContent = best.row.getAttribute('data-size') || best.row.querySelector('th, td').textContent.trim();

      var total = Object.keys(entered).length;
      if (best.hits === total) {
        noteEl.textContent = 'Every measurement falls inside this size.';
      } else if (best.hits > 0) {
        noteEl.textContent = 'Closest match. You are between sizes — size up for a relaxed fit.';
      } else {
        noteEl.textContent = 'Nothing matched exactly. This is the nearest size — the fit team can help.';
      }

      best.row.scrollIntoView({ block: 'nearest' });
    });

    setFinderUnit('in');
    syncFinderFields();
  }

  function boot() {
    document.querySelectorAll('[data-nga-sg]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
